import * as THREE from 'three/webgpu';
import {isSketchContour, isSketchPointObject, isSketchSlotObject} from '../../mesh';
import {
    AbstractInteractionVisualState,
    GeometryInteractionState,
    type InteractionVisualStateContext,
    type InteractionVisualStateRuntime,
    MaterialApplySource,
} from './AbstractInteractionVisualState';
import type {Viewer} from '../../viewer/Viewer';

//------ 草图默认转状态 开始------
/**
 * 草图默认转状态。
 * 说明：
 * 1. 当前仅作为默认状态占位类；
 * 2. 全部默认视觉能力已下沉到 AbstractInteractionVisualState；
 * 3. 如需默认态差异化，可在本类覆写基类方法。
 */
export class SketchDefaultTransitionState extends AbstractInteractionVisualState {
    /**
     * 构造函数。
     * @param runtime 状态运行时。
     * @param viewer Viewer 实例。
     */
    public constructor(runtime: InteractionVisualStateRuntime, viewer: Viewer) {
        super(runtime, viewer);
    }

    /**
     * 刷新视觉状态（草图默认模式专用）。
     * 说明：
     * 1. 保留基类默认刷新逻辑；
     * 2. 补充“选中轮廓 -> 关联边 Selected”能力；
     * 3. “移入轮廓 -> 关联边 Hover”继续复用基类已有逻辑。
     * @param context 当前刷新上下文。
     */
    public override refreshVisualState(context: InteractionVisualStateContext): void {
        super.refreshVisualState(context);
        this.applySelectedContourLinkedEdgesState(context);
    }

    //------ 槽口轮廓选中派生点隐藏 开始------
    /**
     * 判断 Group 状态递归时是否允许处理当前子对象。
     *
     * 设计说明：
     * 1. 槽口轮廓选中语义是“选择槽口整体”，不是逐个选中其边上的派生辅助点；
     * 2. 直线中点、圆弧中点、圆弧象限点属于捕捉/编辑辅助点，整体选中时不应被强制显示；
     * 3. 这里仅拦截槽口 Group 的 Selected 递归，不影响用户直接点选这些点时的直接交互反馈。
     *
     * @param groupObject 当前正在递归应用状态的父级 Group。
     * @param childObject 当前待处理的子对象。
     * @param targetState 本次应用的目标交互状态。
     * @param source 本次状态应用来源。
     * @param context 当前刷新上下文。
     * @returns 允许继续处理该子对象时返回 true。
     */
    protected override shouldApplyObjectStateToGroupChild(
        groupObject: THREE.Group,
        childObject: THREE.Object3D,
        targetState: GeometryInteractionState,
        source: MaterialApplySource,
        context: InteractionVisualStateContext,
    ): boolean {
        void source;
        void context;
        if (!isSketchSlotObject(groupObject) || targetState !== GeometryInteractionState.Selected) {
            return true;
        }
        if (!isSketchPointObject(childObject)) {
            return true;
        }

        /**
         * 槽口边对象会创建中点与象限点作为派生拓扑点；
         * 这些点默认隐藏，整体选中槽口时应继续保持隐藏。
         */
        return !childObject.isMidPoint && !childObject.isQuadrantPoint;
    }
    //------ 槽口轮廓选中派生点隐藏 结束------

    /**
     * 对选中轮廓的关联边应用选中态。
     * @param context 当前刷新上下文。
     */
    private applySelectedContourLinkedEdgesState(context: InteractionVisualStateContext): void {
        context.selectedObjects.forEach((selectedObject) => {
            if (!isSketchContour(selectedObject)) {
                return;
            }

            selectedObject.getLinkObjects().forEach((edgeObject) => {
                this.setObjectState(
                    edgeObject,
                    GeometryInteractionState.Selected,
                    MaterialApplySource.Direct,
                    context,
                );
            });
        });
    }
}
